import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import './ControlPrimitives.css';

type Props = {
  segmentOverlap: number;
  setSegmentOverlap: (v: number) => void;
  maxOverlapSeconds: number;
  isFullscreen?: boolean;
  controlHeight?: number;
};

type StepDirection = -1 | 1;

const OVERLAP_EPSILON = 0.0001;
const OVERLAP_STEPPER_MIN_SECONDS = 0;
const OVERLAP_STEPPER_MAX_SECONDS = 5;
const OVERLAP_STEPPER_SECONDS = 1;

const roundOverlap = (value: number, precision = 100): number => (
  Number((Math.round(value * precision) / precision).toFixed(4))
);

const clamp = (value: number, min: number, max: number): number => (
  Math.min(max, Math.max(min, value))
);

const areOverlapSecondsEqual = (a: number, b: number): boolean => (
  Math.abs(a - b) <= OVERLAP_EPSILON
);

export const formatOverlapSecondsValue = (value: number): string => {
  const normalized = Number.isFinite(value) ? Math.max(0, roundOverlap(value)) : 0;
  const text = normalized.toFixed(2).replace(/\.?0+$/, '');
  return `${text}s`;
};

export const parseManualOverlapInput = (rawValue: string): number | null => {
  const trimmed = rawValue.trim().toLowerCase();
  const withoutSuffix = trimmed.endsWith('s')
    ? trimmed.slice(0, -1).trim()
    : trimmed;

  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(withoutSuffix)) {
    return null;
  }

  const parsed = Number(withoutSuffix);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export const getOverlapStepperSeconds = (
  currentOverlapSeconds: number,
  direction: StepDirection,
  maxOverlapSeconds: number
): number => {
  const current = Number.isFinite(currentOverlapSeconds) ? currentOverlapSeconds : 0;
  const manualMax = Number.isFinite(maxOverlapSeconds) ? Math.max(0, maxOverlapSeconds) : 0;
  const stepperMax = Math.min(OVERLAP_STEPPER_MAX_SECONDS, manualMax);

  if (stepperMax <= OVERLAP_STEPPER_MIN_SECONDS) {
    return clamp(current, OVERLAP_STEPPER_MIN_SECONDS, manualMax);
  }

  if (direction < 0) {
    if (current > stepperMax + OVERLAP_EPSILON) return roundOverlap(stepperMax);
    if (current <= OVERLAP_STEPPER_MIN_SECONDS + OVERLAP_EPSILON) return current;

    const next = Math.floor((current - OVERLAP_EPSILON) / OVERLAP_STEPPER_SECONDS) * OVERLAP_STEPPER_SECONDS;
    return roundOverlap(clamp(next, OVERLAP_STEPPER_MIN_SECONDS, stepperMax));
  }

  if (current < OVERLAP_STEPPER_MIN_SECONDS - OVERLAP_EPSILON) return OVERLAP_STEPPER_MIN_SECONDS;
  if (current >= stepperMax - OVERLAP_EPSILON) return current;

  const next = Math.ceil((current + OVERLAP_EPSILON) / OVERLAP_STEPPER_SECONDS) * OVERLAP_STEPPER_SECONDS;
  return roundOverlap(clamp(next, OVERLAP_STEPPER_MIN_SECONDS, stepperMax));
};

const OverlapSlider: React.FC<Props> = ({
  segmentOverlap,
  setSegmentOverlap,
  maxOverlapSeconds,
  isFullscreen = false,
  controlHeight = 44
}) => {
  const compactHeight = Math.max(30, controlHeight);
  const manualMax = Number.isFinite(maxOverlapSeconds) ? Math.max(0, maxOverlapSeconds) : 0;
  const hasOverlapRange = manualMax > OVERLAP_EPSILON;
  const safeOverlap = Number.isFinite(segmentOverlap) ? Math.max(0, segmentOverlap) : 0;
  const displayOverlap = hasOverlapRange ? clamp(safeOverlap, 0, manualMax) : safeOverlap;
  const displayText = formatOverlapSecondsValue(displayOverlap);
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const finalizingManualEditRef = useRef(false);

  useEffect(() => {
    if (!isEditing) {
      finalizingManualEditRef.current = false;
      return;
    }

    setDraftValue(formatOverlapSecondsValue(displayOverlap).replace(/s$/, ''));
  }, [displayOverlap, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const applyOverlap = useCallback((overlapSeconds: number) => {
    if (!Number.isFinite(overlapSeconds) || overlapSeconds < 0) return;

    const nextOverlapSeconds = hasOverlapRange
      ? clamp(overlapSeconds, 0, manualMax)
      : Math.max(0, overlapSeconds);

    setSegmentOverlap(roundOverlap(nextOverlapSeconds));
  }, [hasOverlapRange, manualMax, setSegmentOverlap]);

  const getNextOverlap = useCallback((direction: StepDirection) => (
    getOverlapStepperSeconds(displayOverlap, direction, manualMax)
  ), [displayOverlap, manualMax]);

  const decreaseOverlap = getNextOverlap(-1);
  const increaseOverlap = getNextOverlap(1);
  const canDecrease = hasOverlapRange && !areOverlapSecondsEqual(decreaseOverlap, displayOverlap);
  const canIncrease = hasOverlapRange && !areOverlapSecondsEqual(increaseOverlap, displayOverlap);

  const stepOverlap = useCallback((direction: StepDirection) => {
    const nextOverlap = getNextOverlap(direction);
    if (areOverlapSecondsEqual(nextOverlap, displayOverlap)) return;

    setIsEditing(false);
    applyOverlap(nextOverlap);
  }, [applyOverlap, displayOverlap, getNextOverlap]);

  const startManualEntry = () => {
    if (!hasOverlapRange) return;
    setIsEditing(true);
  };

  const finishManualEntry = (shouldCommit: boolean) => {
    if (finalizingManualEditRef.current) return;
    finalizingManualEditRef.current = true;

    if (shouldCommit) {
      const parsedValue = parseManualOverlapInput(draftValue);
      if (parsedValue !== null) {
        applyOverlap(parsedValue);
      }
    }

    setIsEditing(false);
  };

  const handleChipKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isEditing) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      stepOverlap(-1);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      stepOverlap(1);
    }
  };

  return (
    <div
      className={`preset-chip-button preset-chip-button-overlap overlap-split-chip speed-split-chip preset-chip-button-labeled ${
        isFullscreen ? 'overlap-split-chip-fullscreen speed-split-chip-fullscreen' : ''
      }`}
      style={{ minHeight: `${compactHeight}px` }}
      role="group"
      aria-label="Segment overlap"
      onKeyDown={handleChipKeyDown}
    >
      <button
        type="button"
        className="speed-split-zone speed-split-edge overlap-split-decrease"
        onClick={() => stepOverlap(-1)}
        disabled={!canDecrease}
        title={canDecrease ? 'Decrease overlap' : 'Minimum overlap reached'}
        aria-label="Decrease overlap"
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>

      {isEditing ? (
        <input
          ref={inputRef}
          className="speed-split-input overlap-split-input"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={() => finishManualEntry(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              finishManualEntry(true);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              finishManualEntry(false);
            }
          }}
          aria-label="Overlap manual entry"
          inputMode="decimal"
        />
      ) : (
        <button
          type="button"
          className="speed-split-zone speed-split-value-zone overlap-split-value-zone"
          onClick={startManualEntry}
          disabled={!hasOverlapRange}
          title={hasOverlapRange ? `Edit overlap, maximum ${formatOverlapSecondsValue(manualMax)}` : 'Create a segment before editing overlap'}
          aria-label={
            hasOverlapRange
              ? `Edit overlap, current overlap ${displayText}`
              : `Current overlap ${displayText}. Create a segment before editing overlap`
          }
        >
          <span className="preset-chip-label">Overlap</span>
          <span className="preset-chip-value">{displayText}</span>
          {hasOverlapRange && <Pencil className="speed-split-edit-hint" size={11} aria-hidden="true" />}
        </button>
      )}

      <button
        type="button"
        className="speed-split-zone speed-split-edge overlap-split-increase"
        onClick={() => stepOverlap(1)}
        disabled={!canIncrease}
        title={canIncrease ? 'Increase overlap' : 'Maximum stepper overlap reached'}
        aria-label="Increase overlap"
      >
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  );
};

export default OverlapSlider;
